// libs
import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import htmlReactParser, { domToReact } from 'html-react-parser'

import { linkifyText } from '@aytm/app/stats/lib/linkify-text'
import { sanitizeNoteNode } from '@aytm/app/stats/lib/note-html'

import { QUESTION_ANNOTATION_LIMIT } from '@aytm/app/stats/page/question-views/components/lib/constants'

import {
  SKIPPER_Q_LINK_NAME,
  SKIPPER_LINK_NAMES
} from '@aytm/app/stats/page/question-views/components/lib/skipper-links/const'
import openSidebarLink, {
  OPEN_SIDEBAR_LINK_NAME,
  checkSidebarLink
} from '@aytm/app/stats/page/question-views/components/lib/skipper-links/open-sidebar'

import {
  prepareForEditing,
  applyEditing
} from '@aytm/app/stats/page/question-views/components/lib/TextEditor/converters'
import linkDecorators from '@aytm/app/stats/page/question-views/components/lib/TextEditor/linkDecorators'
import { getAttributesFromLinkNode } from '@aytm/app/stats/page/question-views/components/lib/skipper-links/attributes'
import {
  onSkipperLinkClick,
  onSkipperLinkSCrollToQuestionClick
} from '@aytm/app/stats/page/question-views/components/lib/skipper-links/click'

// components
import { Button } from 'primereact/button'

import TextEditor from '@aytm/app/stats/page/question-views/components/TextEditor'

// styles
import style from '@aytm/app/stats/page/question-views/components/styles/QuestionAnnotation.module.scss'
import styleTextFormatting from '@aytm/app/stats/page/question-views/components/styles/TextFormatting.module.scss'

const QuestionAnnotation = ({
  className,
  id,
  note,
  editing,
  isNoteEditable,
  isSaveDisabled,
  disabled,
  setNote,
  onSaveEditing,
  onCancelEditing,
  onAIAddQuestionClick
}) => {
  const editingMapsRef = useRef(null)
  if (null === editingMapsRef.current && editing && isNoteEditable) {
    editingMapsRef.current = prepareForEditing(note)
    editingMapsRef.current.decorators = linkDecorators(
      editingMapsRef.current.charsMap,
      editingMapsRef.current.charsRegexp
    )
    setTimeout(() => setNote(editingMapsRef.current.html), 0)
  }

  const transformedNote = useMemo(() => (note ? linkifyText(note) : note), [note])
  const parsedNode = useMemo(() => {
    if (!transformedNote) return transformedNote

    // Handed to the guard as well, so the tags it keeps have their own children re-parsed
    // under the same rules rather than through the parser's default handling.
    const parserOptions = {
      // trim: true,
      replace(domNode) {
        // The ingest stores a note body verbatim, so the save path's tag allow-list is
        // re-applied here before anything is turned into an element.
        const sanitized = sanitizeNoteNode(domNode, parserOptions)
        if (sanitized) return sanitized

        if (SKIPPER_LINK_NAMES.includes(domNode.name)) {
          const linkAttributes = getAttributesFromLinkNode(domNode)
          const title = linkAttributes.title
          const disabled =
            'on' != window.features.reorder_questions?.status ||
            (['vq', 'cvq'].includes(linkAttributes.questionType) && 'on' != window.features.data_lab_vq?.status) ||
            (['cq', 'cvq'].includes(linkAttributes.questionType) && 'on' != window.features.data_lab_cf?.status)

          return (
            <span
              className={classNames(style.annotationLink, {
                [`-dtr-annotation-link -dtr-annotation-link--${linkAttributes.questionType || 'unknown'}`]: !disabled,
                [style.annotationLinkDisabled]: disabled
              })}
              onClick={
                disabled
                  ? null
                  : () => {
                      if (onAIAddQuestionClick) {
                        onSkipperLinkClick({ attributes: linkAttributes, addQuestion: onAIAddQuestionClick })
                      }
                    }
              }
            >
              {'vq' === linkAttributes.questionType ? (
                <i className={`icon-aytmfd-virtual-question-solid-v2 ${style.annotationLinkIcon}`} />
              ) : 'cq' === linkAttributes.questionType ? (
                <i className={`icon-aytmfd-correlation-finder-v2 ${style.annotationLinkIcon}`} />
              ) : 'cvq' === linkAttributes.questionType ? (
                <i className={`icon-aytmfd-correlation-finder-v2 ${style.annotationLinkIcon}`} />
              ) : null}
              {title}
            </span>
          )
        } else if (SKIPPER_Q_LINK_NAME == domNode.name) {
          const id = domNode.attribs?.['q-id'] || ''
          return (
            <span
              className={`${style.annotationLink} -dtr-scroll-to-question-link -dtr-scroll-to-question-link--${id}`}
              onClick={() => void onSkipperLinkSCrollToQuestionClick({ id })}
            >
              {domToReact(domNode.children)}
            </span>
          )
        } else if (OPEN_SIDEBAR_LINK_NAME == domNode.name) {
          const tab = domNode.attribs?.tab || ''
          const focus = domNode.attribs?.focus || ''
          const icon = domNode.attribs?.icon || ''

          return checkSidebarLink({ tab }) ? (
            <span
              className={`${style.annotationLink} -dtr-open-sidebar-link -dtr-open-sidebar-link-${tab} -dtr-open-sidebar-link-${focus}`}
              onClick={() => void openSidebarLink({ tab, focus })}
            >
              {icon ? <i className={`${icon} ${style.annotationLinkIcon}`} /> : null}
              {domToReact(domNode.children)}
            </span>
          ) : (
            <span className={`${style.annotationLink} ${style.annotationLinkDisabled}`}>
              {icon ? <i className={`${icon} ${style.annotationLinkIcon}`} /> : null}
              {domToReact(domNode.children)}
            </span>
          )
        }
        return void 0
      }
    }

    return htmlReactParser(transformedNote, parserOptions)
  }, [transformedNote, onAIAddQuestionClick])

  const onSave = () => {
    if (!disabled) {
      if (editingMapsRef.current) {
        const appliedNote = applyEditing(note, editingMapsRef.current.charsMap, editingMapsRef.current.charsRegexp)
        editingMapsRef.current = null
        onSaveEditing(appliedNote)
      } else {
        onSaveEditing()
      }
    }
  }

  const onCancel = () => {
    editingMapsRef.current = null
    onCancelEditing()
  }

  return note || (editing && isNoteEditable) ? (
    <div className={classNames(style.container, className || '')}>
      {editing ? (
        <>
          {isNoteEditable && (
            <div className={style.row} key="question-storytelling">
              <div className={`${style.editor} p-fluid p-col-12`}>
                <TextEditor
                  text={note}
                  decorators={editingMapsRef.current?.decorators}
                  height={200}
                  characters={QUESTION_ANNOTATION_LIMIT}
                  onChange={setNote}
                />
              </div>
            </div>
          )}
          <div className={style.row}>
            <div className={`${style.group} p-fluid p-right p-col-4`}>
              <Button
                id={`stat-annotation-cancel-button-${id}`}
                className={classNames(
                  'stat-annotation-cancel-button -dtr-stat-annotation-cancel-button',
                  style.button,
                  style.cancel,
                  'p-button-secondary'
                )}
                label="CANCEL"
                onClick={onCancel}
              />
              <Button
                id={`stat-annotation-save-button-${id}`}
                className={classNames('stat-annotation-save-button -dtr-stat-annotation-save-button', style.button)}
                label="SAVE"
                disabled={isSaveDisabled || disabled}
                onClick={isSaveDisabled || disabled ? null : onSave}
              />
            </div>
          </div>
        </>
      ) : (
        <div className={style.row}>
          <div className={`${style.text} ${styleTextFormatting.text}`}>{parsedNode}</div>
        </div>
      )}
    </div>
  ) : null
}

QuestionAnnotation.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  note: PropTypes.string,
  editing: PropTypes.bool,
  isNoteEditable: PropTypes.bool,
  isSaveDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
  setNote: PropTypes.func,
  onSaveEditing: PropTypes.func,
  onCancelEditing: PropTypes.func,
  onAIAddQuestionClick: PropTypes.func
}

QuestionAnnotation.displayName = 'QuestionAnnotation'

export default QuestionAnnotation
